/* eslint-disable no-underscore-dangle */
import i18next from 'i18next'
import {
  StatementRow,
  DimensionStetementsResponse,
  DimensionQueryParams,
  FinacialStatementType,
  BalanceRegistry,
  InvestmentReportRow,
  AmendmentReportRow
} from '../types'
import { ReportResponseItem } from '../../../redux/context/reports/types'
import { extractDataValue } from '../../../shared/utils/balanceRegistry'

const STATEMENT_TYPE_TO_NAMESPACE: Record<FinacialStatementType, string> = {
  incomeStatement: 'incomeStatement',
  assets: 'balanceSheet',
  liabilities: 'balanceSheet'
}

// Map API statementType values to FinacialStatementType
const API_STATEMENT_TYPE_MAP: Record<string, FinacialStatementType> = {
  pl: 'incomeStatement', // profit & loss
  bs: 'assets' // balance sheet (we'll use assets as default, could be liabilities too)
}

// Row type enumeration for financial statements
export enum FinancialRowType {
  HEADING = 'HEADING', // Section titles for grouping accounts (e.g., "Revenue", "Expenses")
  ACCOUNT = 'ACCOUNT', // Regular line items (e.g., "Net Turnover", "Salaries")
  DIMENSION = 'DIMENSION', // Dimension breakdown rows - accounts or headings with dimension definition
  DIMENSION_GROUP = 'DIMENSION_GROUP', // Group row for dimensions under headings/accounts
  INVESTMENT_MENU = 'INVESTMENT_MENU', // Investment action menu row for investable accounts
  INVESTMENT = 'INVESTMENT', // Investment data rows under investment menu
  AMENDMENT_MENU = 'AMENDMENT_MENU', // Amendment action menu row for amendable accounts
  AMENDMENT = 'AMENDMENT', // Amendment data rows under amendment menu
  COUNTER_AMENDMENT_MENU = 'COUNTER_AMENDMENT_MENU', // Counter amendment menu row for counterAmendable accounts
  REALLOCATION_MENU = 'REALLOCATION_MENU', // Reallocation menu row for reallocated accounts
  REALLOCATION = 'REALLOCATION' // Reallocation data rows (unallocated, sources, targets)
}

// Row behavior configuration
export interface RowBehavior {
  expandable?: boolean // Can this row be expanded/collapsed
  showCode?: boolean // Show account code in name
}

export interface EnhancedStatementRow extends Omit<StatementRow, 'id' | 'code' | 'accounts'> {
  // Tree structure
  key: string
  path: string[]

  // Row classification
  rowType: FinancialRowType

  // Optional id (present for real rows, absent for synthetic rows like DIMENSION_GROUP)
  id?: number

  // Source tracking for dimension rows
  _sourceAccountCode?: number
  _sourceStatementRowId?: number
  _sourcePath?: string[]
  _isDimensionData?: boolean
  _childrenLoaded?: boolean // Flag to indicate async children have been loaded (even if empty)

  // Input values for budget editing (user-entered values)
  newBudgets?: BalanceRegistry

  dimensionId?: string | null
  dimensionName?: string // Name of the dimension (not the account name)
  isLoading?: boolean
  expandable?: boolean
  investmentId?: number // Investment ID for investment rows
  date?: string // Date for investment rows
  value?: number // Value for investment rows

  // Async data configuration for menu/group rows
  asyncConfig?: {
    dataRowType: FinancialRowType
    getQueryKey: (params: {
      accountCode?: number
      budgetingScenarioId?: number | null
      statementRowId?: number
    }) => Promise<readonly unknown[]> | readonly unknown[]
    fetchData: (params: {
      companyId: string
      accountCode?: number
      budgetingScenarioId?: number | null
      periodGroups?: any[]
      statementRowId?: number
    }) => Promise<any[]>
    transformData: (data: any[], menuRow: EnhancedStatementRow) => EnhancedStatementRow[]
    sortData?: (a: any, b: any) => number
  }
}

// Create enhanced heading row with translated name
const createEnhancedHeadingRow = (
  heading: StatementRow,
  path: string[],
  statementType?: FinacialStatementType,
  dimensionId?: string | null
): EnhancedStatementRow => {
  // Translate heading name
  let { name } = heading
  if (statementType) {
    const namespace = STATEMENT_TYPE_TO_NAMESPACE[statementType]
    const translationKey = `${namespace}:${heading.id}`
    name = i18next.t(translationKey)
  }

  return {
    ...heading,
    name,
    key: `HEADING-${heading.id}`,
    path,
    dimensionId,
    rowType: FinancialRowType.HEADING,
    _sourceAccountCode: undefined,
    _sourceStatementRowId: heading.id
  }
}

// Create enhanced account row
const createEnhancedAccountRow = (
  account: StatementRow,
  path: string[],
  headingId: number,
  dimensionId?: string | null
): EnhancedStatementRow => {
  return {
    ...account,
    name: account.name,
    key: `ACCOUNT-${account.code}`,
    path,
    dimensionId,
    rowType: FinancialRowType.ACCOUNT,
    _sourceAccountCode: Number(account.code),
    _sourceStatementRowId: headingId
  }
}

// Normalize hierarchical statement data structure into flat array
// Input structure: Heading[] where each Heading contains Accounts[]
// Output: Flat array of enhanced rows [Heading1, Account1, Account2, Heading2, Account3, ...]
export const normalizeStatementRows = (
  headings?: StatementRow[],
  parentPath: string[] = [],
  statementType?: FinacialStatementType,
  dimensionId?: string | null
): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  if (!headings) {
    return result
  }

  // Flatten two-level hierarchy: for each heading, add heading row + all its account rows
  headings.forEach(heading => {
    const headingPath = [...parentPath, heading.id.toString()]
    const enhancedHeading = createEnhancedHeadingRow(heading, headingPath, statementType, dimensionId)
    result.push(enhancedHeading)

    // Add all accounts under this heading
    if (heading.accounts && heading.accounts.length > 0) {
      heading.accounts.forEach(account => {
        const accountPath = [...headingPath, account.code.toString()]
        result.push(createEnhancedAccountRow(account, accountPath, heading.id, dimensionId))
      })
    }
  })

  return result
}

// Function to create a dimension group row for headings and accounts
export const createDimensionGroupRow = (parentRow: EnhancedStatementRow): EnhancedStatementRow => {
  const groupKey = `${parentRow.key}-dimension-group`
  const groupPath = [...parentRow.path, groupKey]

  return {
    name: i18next.t('global:dimensions'),
    key: groupKey,
    path: groupPath,
    rowType: FinancialRowType.DIMENSION_GROUP,
    // Source tracking for dimension group
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path,
    _isDimensionData: false,
    // Clear data properties since this is a grouping row
    expandable: true
  }
}

// Function to create an investment data row from report response
export const createInvestmentRow = (
  investment: InvestmentReportRow,
  parentRow: EnhancedStatementRow
): EnhancedStatementRow => {
  const key = `${parentRow.key}-investment-${investment.id}`
  const path = [...parentRow.path, key]

  return {
    ...investment, // Spread all BalanceRegistry fields (budget, actuals, forecast, etc.)
    name: investment.name || i18next.t('global:investment'),
    key,
    path,
    rowType: FinancialRowType.INVESTMENT,
    id: investment.id,
    // Source tracking
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow._sourcePath
  } as EnhancedStatementRow
}

// Function to transform investment response to enhanced statement rows
export const transformInvestmentsToEnhancedRows = (
  response: InvestmentReportRow[],
  parentRow: EnhancedStatementRow
): EnhancedStatementRow[] => {
  if (!Array.isArray(response)) {
    return []
  }

  return response.map(item => {
    const investmentRow = createInvestmentRow(item, parentRow)

    // Investment data comes in 'budget' but we need it available for all column types
    // (actuals, budget, forecast). Copy budget data to all registries.
    if (investmentRow.budget) {
      // Copy budget data to actuals registry
      if (!investmentRow.actuals) {
        investmentRow.actuals = { elements: {}, groups: {}, length: 0 }
      }
      investmentRow.actuals.elements = { ...investmentRow.budget.elements }
      investmentRow.actuals.groups = { ...investmentRow.budget.groups }
      investmentRow.actuals.length = investmentRow.budget.length

      // Copy budget data to forecast registry
      if (!investmentRow.forecast) {
        investmentRow.forecast = { elements: {}, groups: {}, length: 0 }
      }
      investmentRow.forecast.elements = { ...investmentRow.budget.elements }
      investmentRow.forecast.groups = { ...investmentRow.budget.groups }
      investmentRow.forecast.length = investmentRow.budget.length
    }

    return investmentRow
  })
}

// Type for reallocation data (unallocated, sources, targets)
type ReallocationType = 'unallocated' | 'sources' | 'targets'

// Function to create a reallocation data row (unallocated, sources, or targets)
const createReallocationRow = (
  parentRow: EnhancedStatementRow,
  type: ReallocationType,
  menuPath: string[]
): EnhancedStatementRow => {
  const rowKey = `${parentRow.key}-${type}`
  const rowPath = [...menuPath, rowKey]
  const translation = `financialStatementsPage:reallocation-${type}`

  return {
    name: i18next.t(translation),
    key: rowKey,
    path: rowPath,
    rowType: FinancialRowType.REALLOCATION,
    id: parentRow.id,
    // Copy reallocation-specific data
    actuals: parentRow[type]?.actuals,
    forecast: parentRow[type]?.forecast,
    budget: parentRow[type]?.budget,
    budgets: parentRow[type]?.budgets,
    // Source tracking
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path
  }
}

// Function to create a reallocation menu row with children for reallocated accounts
export const createReallocationMenuRow = (parentRow: EnhancedStatementRow): EnhancedStatementRow => {
  const menuKey = `${parentRow.key}-reallocation-menu`
  const menuPath = [...parentRow.path, menuKey]

  return {
    name: parentRow.name,
    key: menuKey,
    path: menuPath,
    rowType: FinancialRowType.REALLOCATION_MENU,
    id: parentRow.id,
    // Source tracking
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path
  }
}

// Function to create a single dimension row from dimension data
export const createDimensionRow = (
  dimensionData: ReportResponseItem<StatementRow>,
  params: DimensionQueryParams,
  dimensionMap: any,
  dimensionList: any[] = []
): EnhancedStatementRow => {
  const { dimensionKey, data: dimensionRow } = dimensionData
  // Generate key with HEADING- or ACCOUNT- prefix to match parent row format
  const prefix = params.accountCode ? 'ACCOUNT' : 'HEADING'
  const sourceId = params.accountCode ?? params.statementRowId

  const key = `${prefix}-${sourceId}-${dimensionKey}`
  const dimensionId = dimensionKey ?? null
  const expandable = dimensionMap?.has(dimensionId)

  // Find dimension name from dimensionList
  const dimension = dimensionList.find(d => d.dimensionId === dimensionId)
  const dimensionName = dimension?.name

  // Translate name if this dimension row has a statementType (meaning it's from a heading)
  let { name } = dimensionRow
  if (dimensionRow.statementType && dimensionRow.id) {
    const statementType = API_STATEMENT_TYPE_MAP[dimensionRow.statementType]
    if (statementType) {
      const namespace = STATEMENT_TYPE_TO_NAMESPACE[statementType]
      const translationKey = `${namespace}:${dimensionRow.id}`
      name = i18next.t(translationKey)
    }
  }

  return {
    ...dimensionRow,
    name,
    // Enhanced row properties
    key,
    path: [...(params.path || []), key],
    dimensionId: dimensionKey,
    dimensionName,
    rowType: FinancialRowType.DIMENSION,
    expandable,
    // Add metadata about which row this dimension data belongs to
    _sourceAccountCode: params.accountCode,
    _sourceStatementRowId: params.statementRowId,
    _sourcePath: params.path,
    _isDimensionData: true
  } as EnhancedStatementRow
}

// Function to transform dimension response into enhanced statement rows
export const transformDimensionResponseToEnhancedRows = (
  response: DimensionStetementsResponse,
  params: DimensionQueryParams,
  dimensionMap: any,
  dimensionList: any[] = []
): EnhancedStatementRow[] => {
  if (!Array.isArray(response)) {
    return []
  }

  return response.map(item => createDimensionRow(item, params, dimensionMap, dimensionList))
}

// Function to create an amendment data row from report response
export const createAmendmentRow = (
  amendment: AmendmentReportRow,
  parentRow: EnhancedStatementRow
): EnhancedStatementRow => {
  const key = `${parentRow.key}-amendment-${amendment.id}`
  const path = [...parentRow.path, key]

  return {
    ...amendment, // Spread all BalanceRegistry fields (budget, actuals, forecast, etc.)
    name: amendment.name || i18next.t('global:amendment'),
    key,
    path,
    rowType: FinancialRowType.AMENDMENT,
    id: amendment.id,
    // Source tracking
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow._sourcePath
  } as EnhancedStatementRow
}

// Function to transform amendment response to enhanced statement rows
export const transformAmendmentsToEnhancedRows = (
  response: AmendmentReportRow[],
  parentRow: EnhancedStatementRow
): EnhancedStatementRow[] => {
  if (!Array.isArray(response)) {
    return []
  }

  return response.map(item => {
    const amendmentRow = createAmendmentRow(item, parentRow)

    // Amendment data comes in 'budget' but we need it available for all column types
    // (actuals, budget, forecast). Copy budget data to all registries.
    if (amendmentRow.budget) {
      // Copy budget data to actuals registry
      if (!amendmentRow.actuals) {
        amendmentRow.actuals = { elements: {}, groups: {}, length: 0 }
      }
      amendmentRow.actuals.elements = { ...amendmentRow.budget.elements }
      amendmentRow.actuals.groups = { ...amendmentRow.budget.groups }
      amendmentRow.actuals.length = amendmentRow.budget.length

      // Copy budget data to forecast registry
      if (!amendmentRow.forecast) {
        amendmentRow.forecast = { elements: {}, groups: {}, length: 0 }
      }
      amendmentRow.forecast.elements = { ...amendmentRow.budget.elements }
      amendmentRow.forecast.groups = { ...amendmentRow.budget.groups }
      amendmentRow.forecast.length = amendmentRow.budget.length
    }

    return amendmentRow
  })
}

// Function to create an investment menu row for investable accounts
export const createInvestmentMenuRow = (parentRow: EnhancedStatementRow): EnhancedStatementRow => {
  const menuKey = `${parentRow.key}-investment-menu`
  const menuPath = [...parentRow.path, menuKey]

  return {
    name: i18next.t('global:investments'),
    key: menuKey,
    path: menuPath,
    rowType: FinancialRowType.INVESTMENT_MENU,
    // Copy parent properties needed for modals
    id: parentRow.id,
    depreciable: parentRow.depreciable,
    // Source tracking for investment menu
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path,
    expandable: true,
    // Async data configuration
    asyncConfig: {
      dataRowType: FinancialRowType.INVESTMENT,
      getQueryKey: async ({ accountCode, budgetingScenarioId }) => {
        // Use the same query key factory to ensure consistency
        const { investmentQueryKeys } = await import('../../investment/queries/investmentQueryKeys')
        return investmentQueryKeys.report({ accountCode: accountCode!, budgetingScenarioId })
      },
      fetchData: async ({ companyId, accountCode, budgetingScenarioId, periodGroups }) => {
        // Import dynamically to avoid circular dependencies
        const { fetchInvestmentData } = await import('../queries/useDimensionDataQuery')
        return fetchInvestmentData(companyId, accountCode!, budgetingScenarioId, periodGroups)
      },
      transformData: (data, menuRow) => transformInvestmentsToEnhancedRows(data, menuRow),
      sortData: (a, b) => a.id - b.id
    }
  }
}

// Function to create an amendment menu row for amendable accounts
export const createAmendmentMenuRow = (parentRow: EnhancedStatementRow): EnhancedStatementRow => {
  const menuKey = `${parentRow.key}-amendment-menu`
  const menuPath = [...parentRow.path, menuKey]

  return {
    name: i18next.t('global:amendments'),
    key: menuKey,
    path: menuPath,
    rowType: FinancialRowType.AMENDMENT_MENU,
    // Copy parent properties needed for modals
    id: parentRow.id,
    // Source tracking for amendment menu
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path,
    expandable: true,
    // Async data configuration
    asyncConfig: {
      dataRowType: FinancialRowType.AMENDMENT,
      getQueryKey: async ({ accountCode, budgetingScenarioId }) => {
        const { getAmendmentQueryKey } = await import('../hooks/useAmendmentLoading')
        return getAmendmentQueryKey(accountCode!, budgetingScenarioId)
      },
      fetchData: async ({ companyId, accountCode, budgetingScenarioId, periodGroups }) => {
        const { fetchAmendmentData } = await import('../queries/useDimensionDataQuery')
        return fetchAmendmentData(companyId, {
          accountCode: accountCode!,
          budgetingScenarioId,
          periodGroups
        })
      },
      transformData: (data, menuRow) => transformAmendmentsToEnhancedRows(data, menuRow),
      sortData: (a, b) => a.id - b.id
    }
  }
}

// Function to create a counter amendment menu row for counterAmendable accounts
export const createCounterAmendmentMenuRow = (parentRow: EnhancedStatementRow): EnhancedStatementRow => {
  const menuKey = `${parentRow.key}-counter-amendment-menu`
  const menuPath = [...parentRow.path, menuKey]

  return {
    name: i18next.t('financialStatementsPage:amendment-deduction-counter-part'),
    key: menuKey,
    path: menuPath,
    rowType: FinancialRowType.COUNTER_AMENDMENT_MENU,
    // Copy parent properties needed for modals
    id: parentRow.id,
    // Source tracking for counter amendment menu
    _sourceAccountCode: parentRow._sourceAccountCode,
    _sourceStatementRowId: parentRow._sourceStatementRowId,
    _sourcePath: parentRow.path,
    expandable: true,
    // Async data configuration
    asyncConfig: {
      dataRowType: FinancialRowType.AMENDMENT,
      getQueryKey: async ({ accountCode, budgetingScenarioId }) => {
        const { getCounterAmendmentQueryKey } = await import('../hooks/useAmendmentLoading')
        return getCounterAmendmentQueryKey(accountCode!, budgetingScenarioId)
      },
      fetchData: async ({ companyId, accountCode, budgetingScenarioId, periodGroups }) => {
        const { fetchCounterAmendmentData } = await import('../queries/useDimensionDataQuery')
        return fetchCounterAmendmentData(companyId, {
          accountCode: accountCode!,
          budgetingScenarioId,
          periodGroups
        })
      },
      transformData: (data, menuRow) => transformAmendmentsToEnhancedRows(data, menuRow),
      sortData: (a, b) => a.id - b.id
    }
  }
}

// Function to add investment menu rows for investable accounts
export const addInvestmentMenuRows = (rows: EnhancedStatementRow[]): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  rows.forEach(row => {
    // Add the original row
    result.push(row)

    // Add investment menu for ACCOUNT rows that are investable
    if (row.rowType === FinancialRowType.ACCOUNT && row.investable === true) {
      result.push(createInvestmentMenuRow(row))
    }
  })

  return result
}

// Function to add amendment menu rows for amendable accounts
export const addAmendmentMenuRows = (rows: EnhancedStatementRow[]): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  rows.forEach(row => {
    // Add the original row
    result.push(row)

    // Add amendment menu for ACCOUNT rows that are amendable
    if (row.rowType === FinancialRowType.ACCOUNT && row.amendable === true) {
      result.push(createAmendmentMenuRow(row))
    }
  })

  return result
}

// Function to add counter amendment menu rows for counterAmendable accounts
export const addCounterAmendmentMenuRows = (rows: EnhancedStatementRow[]): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  rows.forEach(row => {
    // Add the original row
    result.push(row)

    // Add counter amendment menu for ACCOUNT rows that are counterAmendable
    if (row.rowType === FinancialRowType.ACCOUNT && row.counterAmendable === true) {
      result.push(createCounterAmendmentMenuRow(row))
    }
  })

  return result
}

// Function to add dimension groups to HEADING and ACCOUNT rows
export const addDimensionGroups = (rows: EnhancedStatementRow[]): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  rows.forEach(row => {
    // Add the original row
    result.push(row)

    // Add dimension group for HEADING and ACCOUNT rows
    if ((row.rowType === 'HEADING' && !row.isParent) || row.rowType === 'ACCOUNT') {
      result.push(createDimensionGroupRow(row))
    }
  })

  return result
}

// Function to add reallocation menu rows with children for reallocated accounts
export const addReallocationMenuRows = (rows: EnhancedStatementRow[]): EnhancedStatementRow[] => {
  const result: EnhancedStatementRow[] = []

  rows.forEach(row => {
    // Add the original row
    result.push(row)

    // Add reallocation menu for ACCOUNT rows that are reallocated and have targets
    if (row.rowType === FinancialRowType.ACCOUNT && row.reallocated === true && row.targets) {
      const menuRow = createReallocationMenuRow(row)
      const menuPath = menuRow.path

      // Add reallocation menu row
      result.push(menuRow)

      // Add children rows for unallocated, sources, and targets
      result.push(createReallocationRow(row, 'unallocated', menuPath))
      result.push(createReallocationRow(row, 'sources', menuPath))
      result.push(createReallocationRow(row, 'targets', menuPath))
    }
  })

  return result
}

// Helper function to check if a row has data values for specific columns
export const checkRowHasDataForColumns = (row?: EnhancedStatementRow, columns = [] as any[]): boolean => {
  // Check if row has actual data
  for (const column of columns) {
    const { dataType } = column

    if (dataType && column?.dataKey !== undefined) {
      const value = extractDataValue(row, column)

      if (value !== null && value !== undefined && value !== 0) {
        return true
      }
    }
  }
  return false
}
